<template>
  <div id="forumModal" class="offcanvas-background">
    <div class="offcanvas offcanvas-right"
       v-click-outside="close">
      <div class="offcanvas-header d-flex align-items-center justify-content-between pb-4">
        <div class="d-flex align-items-center">
          <inline-svg :src="require('@/assets/images/icons/Icon_Topic.svg')"
                      width="40" height="40"
                      class="w-40px mr-3" alt="topic-icon"/>
          <span class="font-weight-light" style="font-size: 2.5rem">
            {{ $t("app.label.forum") }}
          </span>
        </div>
        <span class="btn btn-xs btn-icon btn-hover-primary ml-auto"
              @click="close">
          <inline-svg :src="require('@/assets/images/icons/Icon_Cancel.svg')"
                      width="20" height="20"/>
        </span>
      </div>
      <div class="offcanvas-content h-100 pb-8">
        <button
          @click="toggleNewThread()"
          class="btn btn-primary btn-sm mb-5">
          {{ $t("forum.create.thread") }}
        </button>
        <div class="resources-list">
          <div>
            <div v-for="thread in forum.threads" :key="thread.id"
                 class="flex flex-wrap">
              <div class="mb-5 cursor-hand"  @click="onThreadClick(thread.id)">
                <div class="d-flex mb-2">
                  <div class="user-info__image rounded-circle position-relative mr-2">
                    <div class="avatar-placeholder w-50px h-50px" style="font-size: 24px">
                      {{ formatPrivacyLevel(thread.createdBy,3)}}
                    </div>
                  </div>
                  <div class="d-flex flex-column">
                    <div class="d-flex flex-row">
                      <small class="opacity-75">{{$t('forum.thread.created.by')}}
                        <span class="font-weight-bold">
                      {{ formatPrivacyLevel(thread.createdBy,forum.privacyLevel)}}
                    </span></small>
                      <small class="opacity-75 mx-1"> - </small>
                      <small class="opacity-75">{{ formatDateFull(thread.createdAt) }}</small>
                      <small class="opacity-75 mx-1"> - </small>
                      <small class="opacity-75" v-if="thread.postCount > 1"> {{ thread.postCount }}
                        {{$t('forum.thread.posts')}}</small>
                      <small class="opacity-75" v-else> {{ thread.postCount }}
                        {{$t('forum.thread.post')}}</small>
                    </div>
                    <p class="font-weight-normal mb-2">
                      {{ thread.title | str_limit(130) }}</p>
                  </div>
                </div>
                <button class="btn btn-outline-primary btn-sm position-relative">
                  {{ $t("forum.show.thread") }}
                  <span class="forum-notification font-weight-bold"
                        :class="{'active': thread.unreadedPostCount > 0}">
                    {{ thread.unreadedPostCount }}</span>
                </button>
              </div>
            </div>
          </div>
          <div v-if="createNewThread">
            <h3 class="font-weight-light mb-4">
              {{$t('forum.create.thread')}}
            </h3>
            <form @submit.prevent="createThread">
              <div class="form-group"
                   :class="{'validated' : true}"
              >
                <label>{{$t('forum.thread.title')}}</label>
                <input
                  id="threadtitle"
                  type="text"
                  class="form-control form-control-sm" required>
                <label class="pt-3">{{$t('forum.thread.content')}}</label>
                <TinyMCE id="threadcontent"
                         type="text"
                         class="form-control form-control-sm"
                         style="height: 60vh"
                         :init="{
                   height: 200,
                   menubar: false,
                   statusbar: false,
                   contextmenu: false,
                   skin_url:'/js/skins/ui/oxide-dark',
                   content_css: '/js/skins/content/dark/content.css',
                   plugins: ['lists link paste image emoticons'],
                   toolbar:
                     'undo redo |' +
                      'bold italic underline blockquote |' +
                       'emoticons |' +
                        'numlist bullist |' +
                         'link image',
                   setup:function(ed) {
                     ed.on('Paste Change input Undo Redo', function(e) {
                       onForumMessageChange(ed.getContent());
                     });
                   }
                 }">
                </TinyMCE>
              </div>
              <div
                v-if="canPostForum"
                class="d-flex flex-column mt-5">
                <button class="btn btn-primary btn-sm px-8 text-uppercase mb-2">
                  {{$t('forum.create.thread.validate')}}
                </button>
                <button class="btn btn-outline-primary btn-sm px-8 text-uppercase"
                        @click="toggleNewThread">
                  {{$t('app.button.cancel')}}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <!--end::Content-->
    <ThreadCanvas :class="{'offcanvas-on': showThread}"
                  @close="closeThread(true)"
                  @closeAll="closeThread(false)"/>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import HelperMixin from '@/mixins/helperMixin';
import 'tinymce/tinymce';
import TinyMCE from '@tinymce/tinymce-vue';
import 'tinymce/themes/silver';
import 'tinymce/icons/default';
import 'tinymce/plugins/paste';
import 'tinymce/plugins/emoticons';
import 'tinymce/plugins/emoticons/js/emojis';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/image';
import ThreadCanvas from '@/components/canvas/ThreadCanvas.vue';
import store from '@/store';
import { CREATE_NEW_THREAD, FETCH_FORUMTHREADPOSTS, FETCH_FORUMTHREADS } from '@/store/actions.type';
import { getTinymce } from '@tinymce/tinymce-vue/lib/cjs/main/ts/TinyMCE';

export default {
  name: 'ForumCanvas',
  mixins: [HelperMixin],
  components: {
    ThreadCanvas,
    TinyMCE,
  },
  computed: {
    ...mapGetters(['forum', 'organization']),
  },
  data() {
    return {
      forumModal: true,
      showThread: false,
      createNewThread: false,
      canPostForum: false,
    };
  },
  methods: {
    onThreadClick(threadId) {
      this.isLoading = true;
      store.dispatch(FETCH_FORUMTHREADPOSTS, threadId).then(() => {
        this.showThread = true;
        this.isLoading = false;
        this.$Progress.finish();
        store.dispatch(FETCH_FORUMTHREADS, this.forum.id);
      });
    },
    closeThread(reloadForum) {
      if (reloadForum) {
        this.$parent.loadForum();
      }
      this.showThread = false;
    },
    close() {
      this.createNewThread = false;
      if (document.getElementById('forumModal').classList.contains('offcanvas-on')) {
        this.$emit('close', { src: 'forum' });
      }
    },
    toggleNewThread() {
      this.createNewThread = !this.createNewThread;
    },
    createThread() {
      const messageContent = getTinymce().get('threadcontent').getContent();
      if (messageContent === '') {
        this.$toasted.error(this.$t('help.modal.message.send.error'));
        return;
      }
      const forum = this.forum.id;
      const organization = store.getters.organization.id;
      const title = document.getElementById('threadtitle').value;
      this.$store.dispatch(CREATE_NEW_THREAD,
        {
          messageContent,
          forum,
          title,
          organization,
        }).then(() => {
        this.$toasted.success(this.$t('help.modal.message.send.success'));
        this.createNewThread = false;
        this.$Progress.finish();
      }).catch(() => {
        this.$toasted.error(this.$t('help.modal.message.send.error'));
      });
    },
    onForumMessageChange(message) {
      if (message === '' && this.canPostForum) {
        this.canPostForum = false;
      } else if (!this.canPostForum) {
        this.canPostForum = true;
      }
    },
  },
};
</script>
